import React, { useCallback, useEffect, useMemo, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import  type { EmblaOptionsType, EmblaCarouselType } from "embla-carousel-react"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"
import { cn } from "~/utils/shadecn"

export interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  options?: EmblaOptionsType
}

const Carousel: React.FC<CarouselProps> = ({ options, className, children }: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const [showPrevBtn, setShowPrevBtn] = useState(false)
  const [showNextBtn, setShowNextBtn] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setShowPrevBtn(emblaApi.canScrollPrev())
    setShowNextBtn(emblaApi.canScrollNext())
  }, [])
  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)

    return () => {
      emblaApi.off("reInit", onInit)
      emblaApi.off("reInit", onSelect)
      emblaApi.off("select", onSelect)
    }

  }, [emblaApi, onInit, onSelect])

  const blurStyle = useMemo(() => ({ backdropFilter: "blur(0.8px)" }), [])

  return (
    <div className={cn("my-2 relative group/carousel overflow-x-clip hover:z-10", className)}>
      <div className="mx-8 md:mx-16 relative" ref={emblaRef}>
        <div className="flex relative">{children}</div>
      </div>
      <div className="absolute w-8 md:w-16 h-full flex items-center justify-center left-0 top-0">
        <div className="absolute w-full h-full bg-background/50" style={blurStyle}></div>
        {showPrevBtn && (
          <button
            className="relative invisible group-hover/carousel:visible w-full h-full"
            type="button"
            onClick={scrollPrev}>
            <ChevronLeftIcon className="w-8 md:w-12 h-20 hover:scale-125 md:hover:scale-150 transition-transform text-zinc-500" />
          </button>
        )}
      </div>
      <div className="absolute w-8 md:w-16 h-full flex items-center justify-center right-0 top-0">
        <div className="absolute w-full h-full bg-background/50" style={blurStyle}></div>
        {showNextBtn && (
          <button
            className="relative invisible group-hover/carousel:visible w-full h-full"
            type="button"
            onClick={scrollNext}>
            <ChevronRightIcon className="w-8 md:w-12 h-20 hover:scale-125 md:hover:scale-150 transition-transform text-zinc-500" />
          </button>
        )}
      </div>
    </div>
  )
}

export default React.memo(Carousel)
